import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Gender, RunLogLineDto, RunStateDto } from '@acs/shared'
import { apiGet, apiPost } from '@/lib/api'
import type { WorkerRole } from '@/components/session/hunt-fleet'

/**
 * The client half of the live run channel.
 *
 * ── Seeded once, then pushed ────────────────────────────────────────
 * Both queries here fetch exactly once and are then kept current by `run.state`
 * and `run.append` frames that `use-realtime` merges straight into this cache.
 * Neither polls.
 *
 * That is the whole performance story of this screen. The previous shape polled
 * the run log every second per open console and the running registry every five
 * seconds for the whole table — so a console left open overnight made ~30,000
 * HTTP requests, each one a Mongo read of the entire ring, to discover that
 * nothing had changed. Now a line reaches the screen ~80ms after it is written
 * and an idle console costs nothing at all.
 *
 * `staleTime: Infinity` is what enforces it: without it React Query refetches on
 * mount and on window focus, quietly reintroducing the traffic this design
 * exists to remove. The stream's reconnect handler invalidates `['automation']`
 * after a gap, which is the one and only path that refetches.
 */

/* ------------------------------------------------------------------ *
 * Keys
 * ------------------------------------------------------------------ */

export const RUNS_KEY = ['automation', 'runs'] as const

/** Exported so `use-realtime` writes to exactly the key these hooks read. */
export function runLogKey(customerId: string) {
  return ['automation', 'run-log', customerId] as const
}

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

export type RunLogLine = RunLogLineDto
export type RunState = RunStateDto

/** customerId → its live run state. The table's per-row source of truth. */
export type RunStateMap = Record<string, RunStateDto>

export interface KeepAliveEntry {
  customerId: string
  applicationId: string
  beats: number
  lastState: string | null
  lastOkAgoMs: number | null
  consecutiveFailures: number
  nextInMs: number
  /**
   * The heartbeat is switched off: registered and holding the seat's address,
   * but sending nothing.
   *
   * Must be rendered rather than left to `nextInMs`, which counts down to the
   * dormant wake-up and would otherwise promise a beat that never comes.
   *
   * Optional so an older API — or a cached response mid-deploy — reads as "not
   * dormant" rather than as `undefined` on a required field.
   */
  dormant?: boolean
  /**
   * Dispatch-to-dispatch: how far apart the last two beats actually went out.
   *
   * Runs LONG by however much a WAF detour cost, and that is correct rather
   * than drift — a beat that met a 405, bought a token and retried answered
   * later, so the next one is due later.
   */
  lastGapMs?: number
  /**
   * Response-to-dispatch: from the previous beat's LAST response to this beat
   * going out. The number the scheduler exists to hold at the interval, and the
   * one worth watching — it should read the interval on every beat, including
   * the ones that took a detour.
   */
  lastSinceResponseMs?: number
}

/** One completed schedule check, as the live worker view shows it. */
export interface HuntPoll {
  at: number
  worker: number
  egress: string
  /**
   * Which borrowed identity made this check.
   *
   * Consecutive checks from one worker should name DIFFERENT accounts in a
   * repeating order — that is the rotation working. The same name twice in a row
   * means the ring has stalled and one identity is carrying the whole rate,
   * which is what a wall of 429s looks like from the inside.
   */
  account: string
  /**
   * HTTP status, or `0` for a request that never got a reply at all — a reset
   * socket, a dead proxy, a timeout. Treat `0` as a refusal everywhere and
   * exclude it from percentiles; the `body` carries the exception message.
   */
  status: number
  ms: number
  /** Of `ms`, the part spent before the first response byte arrived. */
  ttfbMs: number
  /** Bytes on the wire, and whether they were compressed. */
  bytes: number
  enc: string
  /** False when this check also paid a TCP + proxy + TLS handshake. */
  warm: boolean
  /** This system's reading of the body. */
  scheduleStatus: string | null
  active: boolean
  /** What the server actually said, clipped. Empty on older records. */
  body: string
}

/**
 * One call, as a log line needs it.
 *
 * Deliberately smaller than `HuntPoll` — no body, size or encoding. Two hundred
 * of these per worker is the stream; the detail of any one check lives in the
 * Requests and Errors views.
 */
export interface WorkerCallDto {
  /** This worker's call number in the run, so a GAP in the log is visible. */
  seq: number
  at: number
  /** The `session_accounts` `_id`, and its login. Empty id = the own token. */
  accountId: string
  account: string
  status: number
  ms: number
  ttfbMs: number
  egress: string
  /**
   * What the schedule said — `INACTIVE` on almost every line.
   *
   * Null when the call failed, or when the field could not be read off the
   * body. That second case is worth telling apart from a closed shift: a run
   * reporting nothing for hours looks identical whether the shift never opened
   * or the document changed shape and this system stopped understanding it.
   * Optional because a snapshot retained before this shipped will not carry it.
   */
  scheduleStatus?: string | null
  /** The lines that matter: this check saw the shift open. */
  active?: boolean
}

/**
 * One shift-list search, as the inspector reads it.
 *
 * Carries a response BODY, which `WorkerCallDto` deliberately does not — and
 * that is the whole reason it exists separately. This detector's failure mode is
 * a 200 in 180ms carrying an empty list, which is invisible in a status code and
 * identical to a job that is genuinely advertising nothing. `cards` and the body
 * are the only two fields that separate them.
 *
 * Only the newest few calls per worker keep a body; the rest carry `''`. A
 * MATCH keeps its body wherever it sits, because it is the record the run is
 * judged by.
 */
export interface SearchCallDto {
  at: number
  /** Which worker made it — load-bearing in the whole-hunt match list. */
  worker: number
  /**
   * This call's id, in the vocabulary of whichever fleet produced the row.
   *
   * For the SHIFT LIST it is a LOCAL call id that never reached the wire: that
   * fleet's header set was aligned with the sibling bot's, which does not carry
   * the header, and the id is still minted because the inspector keys its rows
   * on it and two calls can land in the same millisecond. Rows kept from the
   * retired job fleet carry the `x-amzn-requestid` they sent.
   */
  reqId: string
  /**
   * How long this call waited for a concurrency slot before it was allowed out.
   *
   * The figure no per-request timing can produce, and the one that exposes a
   * limiter sized below the fleet — it appears as tens or hundreds of
   * milliseconds that nothing else on the row explains.
   */
  queueMs: number
  ms: number
  ttfbMs: number
  status: number
  bytes: number
  /** Cards in the answer. `-1` where the call never got far enough to count. */
  cards: number
  /** Whether this search fired a claim. */
  hit: boolean
  waf: boolean
  egress: string
  /**
   * The headers this call put on the wire, WAF cookie masked.
   *
   * Present only on the rows that still carry a body — `{}` once demoted. Most
   * of them exist to make the request indistinguishable from the portal's own,
   * so when one worker starts collecting 403s and its siblings do not, these and
   * the address are the only two things that differ.
   */
  headers: Record<string, string>
  /** Response text, clipped. `''` on rows whose body has been dropped. */
  body: string
}

/** One worker's sighting of ACTIVE. Rare, and the point of the whole system. */
export interface ActiveHitDto {
  at: number
  worker: number
  /** That worker's own call number, so the sighting can be found in its log. */
  seq: number
  /**
   * Which fleet saw it, stamped by the server that dispatched the worker.
   *
   * What the Active tab's sub-tabs group on. Optional because sightings
   * recorded before this field shipped are still in the store; the tab derives
   * the fleet from the worker index for those, which is what `fleetRole` is for.
   */
  role?: 'watch' | 'shift-list' | 'job'
  accountId: string
  account: string
  egress: string
  ms: number
  ttfbMs: number
  /** Whether this sighting is the one that fired the claim. Exactly one does. */
  struck: boolean
}

/**
 * The `data`-bearing update-application response that won the shift.
 *
 * The single most important object this API returns. `data` is the condition —
 * these endpoints answer rejections with HTTP 200 and `{ data: null, errorCode }`,
 * so a status line proves nothing and the presence of a data object proves
 * everything.
 */
export interface ClaimReceiptDto {
  customerId: string
  /** When the response landed, and when the request that produced it went out. */
  at: number
  sentAt: number
  ms: number
  attempt: number
  worker: number
  /** When a worker saw ACTIVE, and the whole race measured end to end. */
  activeAt: number | null
  activeToOkMs: number | null
  applicationId: string
  currentState: string
  previousState: string | null
  adsState: string | null
  jobId: string | null
  scheduleId: string | null
  selectedAt: string | null
  /** The top-level keys of `data` — the shortest proof the key was there. */
  keys: string[]
  /** The `data` object, pretty-printed and clipped. */
  body: string
}

/**
 * One borrowed identity's whole record.
 *
 * Keyed by account rather than by worker on purpose. A worker has both an
 * address and a ring of identities, so its error count cannot say which of the
 * two the server is objecting to; an identity has only itself. If the refusals
 * follow the account making four times everyone else's requests — while its
 * address is no busier than any other — the limit is on the caller, and
 * rotating endpoints will not touch it.
 */
export interface AccountStatDto {
  account: string
  worker: number
  checks: number
  ok: number
  errors: number
  /** Counts keyed by HTTP status. `0` is a request that never got a reply. */
  byStatus: Record<string, number>
  ttfbTotal: number
  ttfbN: number
  firstAt: number
  lastAt: number
  lastStatus: number
  /** False once it has been retired from its ring — refused, or expiring. */
  live: boolean
  /**
   * Why it was retired: `expiring`, or `http 401`. Null while still in a ring.
   *
   * The distinction is the diagnosis. A column of `expiring` means the POOL is
   * handing out rows with little life left and this run is spending three Mongo
   * writes each to churn through them; `http 401` means the identities are being
   * refused. Struck-through rows read identically without it.
   */
  retiredFor?: string | null
}

/**
 * The live hunt for one customer.
 *
 * Rides on the run-log response rather than a page of its own: a hunt polls tens
 * of times a second and none of that can go in the run log, so this is the only
 * place the per-worker detail exists — and it belongs where the operator is
 * already watching the run.
 */
export interface HuntLiveDto {
  customerId: string
  scheduleId: string
  /** The endpoint every worker is calling. */
  url: string
  state: string
  polls: number
  errors: number
  lastStatus: string | null
  /** Wall-clock start, so elapsed can tick smoothly rather than in poll jumps. */
  startedAt: number
  elapsedMs: number
  rate: number
  meanRttMs: number
  egress: number
  /** Accounts held across every ring. Dips while refused ones are replaced. */
  accounts: number
  /** False for a retained post-mortem: the numbers are frozen, nothing polls. */
  live: boolean
  endedAt: number | null
  endedReason: string | null
  /**
   * Holding station — alive, keeping every endpoint and account, not watching.
   *
   * Set while the session is being signed in again. Distinct from stopped in
   * every way that matters, and it has to be shown: without it a held hunt is a
   * table of workers whose rate has fallen to zero and whose `ago` values are
   * climbing, which is exactly what a broken one looks like.
   */
  paused?: boolean
  pauseReason?: string | null
  /**
   * Every failure, per worker, newest last — index-aligned with `workers`.
   *
   * Whole-hunt rather than windowed, which is the difference that makes it
   * usable: the shared `recent` ring spans under two seconds at five gapless
   * workers, so a worker failing once in three has all of its evidence pushed
   * out by its healthy siblings before anyone can look at it.
   */
  workerErrors?: HuntPoll[][]
  /**
   * Every call, INDEXED BY WORKER, oldest first — the log itself.
   *
   * Only the SELECTED worker's entry is populated while a hunt is live; the
   * others are empty because they were not requested, not because nothing
   * happened. `workerLogCounts` is what says how many each actually holds.
   * A stopped hunt's retained snapshot carries all of them.
   */
  workerLog?: WorkerCallDto[][]
  /** Calls held per worker, always sent — one number each. */
  workerLogCounts?: number[]
  /** Kept failures per worker, always sent, so counts never cost a payload. */
  workerErrorCounts?: number[]
  /** Which worker's records this response carries. Null on a retained snapshot. */
  logWorker?: number | null
  /** The configured depth, so the panel can show "180 of 200". */
  workerLogCap?: number
  /**
   * True when the per-worker records could not be read from the store.
   *
   * The counters and the worker table beside them are computed in the API
   * process and stay correct, so the panel is not blank — only the call and
   * failure lists are missing, and they must say so rather than render as an
   * empty ring.
   */
  logDegraded?: boolean
  /**
   * What each borrowed identity has done, busiest first.
   *
   * The table that separates a per-ADDRESS rate limit from a per-CALLER one —
   * two failures that look identical per worker and have opposite remedies.
   */
  accounts_detail?: AccountStatDto[]
  /**
   * Identities swapped out so far — the RATE the table above cannot show.
   *
   * "25 held, 41 seen" is unremarkable over an hour and a fault in two minutes,
   * and the table alone cannot tell those apart.
   */
  accountSwaps?: number
  /** Whether a full per-check trace is being recorded, and how big it is. */
  trace?: { on: boolean; size: number }
  /**
   * The headline numbers, over the newest `cap` calls across every worker.
   *
   * Computed server-side and sent as six numbers — the alternative is shipping
   * five hundred records to a browser so it can average them. Optional because a
   * post-mortem retained before this shipped will not carry it.
   */
  stats?: {
    n: number
    cap: number
    okN: number
    okAvgMs: number
    s403: number
    s405: number
    s429: number
    /**
     * The same tallies for the WHOLE run — never evicted.
     *
     * Both spans are shown because only one of them is misleading on its own: a
     * refusal older than the window is genuinely gone from the counts above, and
     * beside a whole-run `refused` tile that still reads 1 it looks like a
     * miscount rather than like two different measurements.
     */
    run?: {
      n: number
      okN: number
      okAvgMs: number
      s403: number
      s405: number
      s429: number
      other: number
    }
  }
  /**
   * How many of `workers` read the SCHEDULE. The rest read the shift list.
   *
   * The boundary of one shared index space, shipped as a single integer so a
   * poll record — which carries only a worker number — can still be labelled.
   * Optional so a snapshot retained before this shipped still renders.
   */
  watchWorkers?: number
  /**
   * The watch's claim stand-down — its counter and the cap it is judged against.
   *
   * Always sent, not only while stood down: a hunt at 24 of 25 and one at 0 of 25
   * are completely different situations and every other figure on the panel reads
   * identically for both. `standDown` is derived server-side from the pair, so it
   * cannot disagree with them. Optional for snapshots retained before it shipped.
   */
  claimGate?: {
    failedUpdateStreak: number
    failedUpdateStreakMax: number
    standDown: boolean
  }
  /**
   * The shift-list detector, as one block. Null when this hunt runs none.
   *
   * Deliberately NOT folded into `polls`/`errors` above. Those describe schedule
   * reads and are what the watch is tuned against; these describe a different
   * call to a different backend with different failure modes at a different rate,
   * and one set of numbers covering both would quietly change what every existing
   * figure means. Null rather than zeroes so the panel can leave the section out
   * entirely rather than render a row of zeroes that reads as a broken detector.
   */
  gql?: {
    /** As armed, spares included — the band size every worker's fleet is derived from. */
    workers: number
    /**
     * Spares that stood down once a shift with no UUID got one. Their rows are no
     * longer in `workers`. Optional so an API from before spares still type-checks.
     */
    retired?: number
    polls: number
    errors: number
    /** Sightings — the number that says whether this detector works at all. */
    hits: number
    /** How long since the most recent. Null until there has been one. */
    hitAgeMs: number | null
    /** Cards in the last answer. `-1` before the first one lands. */
    lastCards: number
    /** The most any one answer carried. Reaching the page size is a warning. */
    cardsMax: number
    transients: number
    rotations: number
    stuck: number
    drift: boolean
    /** 200s that carried `errors` and no cards — an accepted request, rejected query. */
    graphErrors: number
    /** The first such message. Null until one arrives. */
    graphError: string | null
    /** Workers currently holding a live minted session. */
    /** Jars taken off the shelf this run, across the fleet. */
    /** Lease attempts that found nothing usable — a dry or unreachable shelf. */
    jobId: string | null
    /** The request every call sends. Constant per hunt; sent once, not per row. */
    request: { url: string; body: string } | null
    /** The focused worker's newest calls. Empty when no worker is focused. */
    calls: SearchCallDto[]
    /** Every match, whole-hunt — not scoped to the focused worker. */
    hitCalls: SearchCallDto[]
  } | null
  /** Why a configured shift-list fleet was not armed. Null in both ordinary cases. */
  gqlOffReason?: string | null
  /**
   * The seat, as the schedule workers last read it. Null on the Brazil flow.
   *
   * `count` is `laborDemandAvailableCount` off the newest parsed schedule body
   * — seeded by the pick-time value, null before anything has been read; `ageMs`
   * is how long ago that was. `emptyRun` is the consecutive reads that reported
   * no free seat, judged against `emptyRunMax` — the "give the shift up after
   * empty reads" setting, 0 when that rule is off. `unparsed` is the finding
   * worth a warning: the newest body carried no count at all, so the run neither
   * advances nor resets, and a schedule that has changed shape looks exactly
   * like one that is quietly full. `assigned` says the pair was set on the
   * customer: the count is shown and never judged, `emptyRun` stays 0, and the
   * shift is never given up.
   *
   * Optional because a view retained before this shipped will not carry it.
   */
  seats?: {
    count: number | null
    ageMs: number | null
    emptyRun: number
    emptyRunMax: number
    unparsed: boolean
    assigned: boolean
  } | null
  /**
   * The GA fleet — `get-all-schedules`, read for a shift's UUID.
   *
   * Null when it is not running, exactly like the `gql` block above: a fleet
   * that is off and a fleet that is on and finding nothing are different facts,
   * and a block of zeroes cannot tell them apart.
   *
   * On a Current run this block appears beside the schedule watch only while
   * the assigned shift has no UUID, and goes null once a listing names it. It
   * used to appear alone, before an application existed, while the run waited
   * for the job to advertise something; the create now goes out at Start and
   * nothing is armed before an application exists.
   */
  ga?: {
    /** As armed, spares included. See the same field on `gql`. */
    workers: number
    /** Spares that stood down once the shift's UUID was found. See `gql.retired`. */
    retired?: number
    polls: number
    errors: number
    /** Reads that came back advertising something. The number that matters. */
    hits: number
    /** How long since the most recent. Null until there has been one. */
    hitAgeMs: number | null
    /**
     * The most recent `availableSchedules.total`.
     *
     * `null` is NOT zero, and the difference is the whole reason this fleet was
     * worth building. Zero is the ordinary answer it waits through; null means no
     * read has produced a number the server understands — a bot check, an error
     * envelope, a truncated body — which on a fleet that has been running a while
     * is the failure that used to be invisible.
     */
    lastTotal: number | null
    transients: number
    rotations: number
    stuck: number
    jobId: string | null
    /** The request every call sends. `body` is real — this one is a POST. */
    request: { url: string; body: string } | null
    /** The focused worker's newest calls. Empty when no worker is focused. */
    calls: SearchCallDto[]
    /** Every sighting, whole-hunt — not scoped to the focused worker. */
    hitCalls: SearchCallDto[]
    /** Mean wait behind this fleet's own limiter — an undersized pool, made visible. */
    queueMs: number
    /** The gap this fleet holds between reads. Never zero, unlike the others. */
    gapMs: number
  } | null
  /** Why a configured GA fleet was not armed. Null in both ordinary cases. */
  gaOffReason?: string | null
  workers: {
    worker: number
    /**
     * Which detector this worker belongs to. Derived server-side from the index
     * by `roleOf`, so it can never disagree with the fleet counts.
     */
    role?: WorkerRole
    egress: string
    /**
     * This worker holds no address, so it is not checking at all.
     *
     * The one state that cannot be inferred from the numbers beside it. A parked
     * worker never reaches the poll loop, so it records nothing and `lastAgoMs`
     * climbs forever — which reads exactly like a socket that never came back,
     * and is the opposite of the truth. `egress` is `'parked'` in this case
     * rather than the address it used to hold.
     */
    parked: boolean
    /** The identity used on the last check, and how many this worker holds. */
    account: string
    accounts: number
    /** Ring size. `1/3` and `3/3` are entirely different health signals. */
    accountsTotal: number
    /** This address's latency floor, and consecutive checks past the server's
     *  own throttle threshold. 0 means no baseline has been established yet.
     *  Not displayed — it only ever falls — but it is what `slow` is judged
     *  against, so the ▲ marker still needs it. */
    bestTtfbMs: number
    /**
     * This worker's share of the rolling window: mean accepted round-trip, and
     * its refusals by status.
     *
     * From the SAME entries as the summary tiles, which is what makes the two
     * agree — a worker's 429 count is its share of exactly the calls the
     * headline number describes, not a separate window that happens to be near
     * it.
     */
    avgMs: number
    s403: number
    s405: number
    s429: number
    slow: number
    polls: number
    errors: number
    /** Times this worker saw the shift open. Whole-run, like `polls`. */
    actives?: number
    lastMs: number
    lastStatus: number
    lastAgoMs: number | null
  }[]
  lastError: {
    at: number
    worker: number
    egress: string
    account: string
    status: number
    ms: number
    url: string
    body: string
  } | null
  recent: HuntPoll[]
  /**
   * Every attempt at the claim — the calls that decide whether a shift was won.
   *
   * Watching is instrumentation; this is the outcome. `update-application` is the
   * only request that changes anything, so each attempt carries what came back
   * verbatim: a lost shift and a malformed request look identical from outside,
   * and only the response body tells them apart.
   */
  strikes: {
    at: number
    attempt: number
    /**
     * The detector whose sighting fired this attempt, and its fleet.
     *
     * Optional because a hunt that was struck before this field existed is
     * still in Redis and still readable in the post-mortem — those rows render
     * exactly as they always did, without a tag, rather than as `wundefined`.
     */
    worker?: number
    role?: 'watch' | 'shift-list' | 'job'
    ms: number
    ok: boolean
    status: number | null
    /** The application state the server reported. `JOB_SELECTED` is the win. */
    state: string | null
    errorCode: string | null
    message: string | null
    body: string
  }[]
  /**
   * Attempts made this run — the number the header counts.
   *
   * NOT `strikes.length`. The server keeps every attempt and ships them a page
   * at a time (a seed, then deltas by cursor), so the array is whatever this
   * console has accumulated so far and the total is what the run actually did.
   * Optional because a retained view from an older server carries neither.
   */
  strikeTotal?: number
  /** Newest attempt id in `strikes`. Handed back as `?strikesAfter=` for a delta. */
  strikeCursor?: string | null
  /** True when the attempts could not be read — the rows are then a short tail. */
  strikesDegraded?: boolean
  /**
   * Every sighting of ACTIVE across every worker, and their total.
   *
   * Whole-run and never windowed. A run that saw three openings and claimed none
   * is a completely different story from one that never saw a shift, and every
   * other field on this object is identical between the two.
   */
  actives?: number
  /**
   * The sightings themselves — a page of the store, accumulated by the console.
   *
   * No longer a window. The server keeps every sighting of a run and ships a
   * seed then deltas, so this grows for as long as the drawer is open. `actives`
   * stays the whole-run total and is what any count should be read from.
   */
  activeHits?: ActiveHitDto[]
  /**
   * Sightings per fleet, whole-run. Their sum is `actives`.
   *
   * Partial: a fleet that never ran may be absent, and the retired job fleet
   * always is. Read each with `?? 0`.
   */
  activesByRole?: Partial<Record<WorkerRole, number>>
  /** Newest sighting id in `activeHits`. Handed back as `?activeAfter=` for a delta. */
  activeCursor?: string | null
  /** True when the sightings could not be read — the rows are then a short tail. */
  activeDegraded?: boolean
  /** The response that won the shift, or null. Survives into the post-mortem. */
  claim?: ClaimReceiptDto | null
}

export interface RunLogResponse {
  customerId: string
  lines: RunLogLine[]
  updatedAt: number
  /** Newest stream id in this response. Handed back as `?after=` to fetch a delta. */
  cursor: string | null
  /**
   * True when the store could not be reached.
   *
   * Distinct from an empty log, and the console must render the difference:
   * "this customer has never run" and "we could not read what they did" look
   * identical otherwise, and they call for opposite responses.
   */
  degraded?: boolean
  keepAlive: KeepAliveEntry | null
  hunt: HuntLiveDto | null
  state: RunState | null
}

interface RunsResponse {
  runs: RunStateDto[]
  at: number
}

/* ------------------------------------------------------------------ *
 * The run map
 * ------------------------------------------------------------------ */

/**
 * Every run this API process knows about, keyed by customer.
 *
 * Stored as a plain object rather than a Map because it lives in the query cache
 * and has to survive structural sharing — React Query compares and clones cached
 * values, and a Map comes back as an empty object.
 */
function runStatesOptions(qc: ReturnType<typeof useQueryClient>) {
  return {
    queryKey: RUNS_KEY,
    queryFn: async (): Promise<RunStateMap> => {
      const res = await apiGet<RunsResponse>('/api/automation/runs')

      const map: RunStateMap = {}
      for (const run of res.runs) map[run.customerId] = run

      /*
       * Merge, do not replace — the seed and the stream race.
       *
       * The fetch is in flight for ~100ms, and `run.state` frames keep arriving
       * throughout. Those frames are written straight into this cache key, so a
       * response that was built BEFORE one of them landed would silently roll
       * that customer back to a staler phase on arrival — and because nothing
       * polls, it would stay rolled back until the next frame for that specific
       * customer, which for a run that just finished is never.
       *
       * Resolved per customer by `updatedAt`, so it is correct in both
       * directions rather than just favouring one source.
       */
      const live = qc.getQueryData<RunStateMap>(RUNS_KEY)
      if (live) {
        for (const id in live) {
          const streamed = live[id]
          const seeded = map[id]
          if (streamed && (!seeded || streamed.updatedAt >= seeded.updatedAt)) {
            map[id] = streamed
          }
        }
      }

      return map
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  }
}

/** The whole map — what the table needs, since it renders every row. */
export function useRunStates() {
  return useQuery(runStatesOptions(useQueryClient()))
}

/**
 * The fleet-wide `workersPerCustomer`, for the Approved list's Workers column.
 *
 * That column shows this number as a PLACEHOLDER on every row with no override
 * of its own, so the table needs it — but it needs nothing else the settings
 * endpoint returns, and it needs it about as often as somebody edits it.
 *
 * Hence a key of its own rather than sharing the settings page's. That page
 * polls the same endpoint every two seconds for its live counters, and reusing
 * its key would silently put a 600-row virtualised table on a 2s refetch to
 * keep one integer current. Here it is fetched once per mount, held for a
 * minute, and re-read when the window is focused — which covers the real case,
 * an operator changing the setting in another tab and coming back.
 *
 * `DEFAULT_WORKERS_FALLBACK` is what the placeholder reads before the first
 * response lands. It matches the shipped default, so the common case shows the
 * right number immediately and a mismatch corrects itself within one fetch.
 */
export const DEFAULT_WORKERS_KEY = ['automation', 'hunt-settings', 'workers'] as const
const DEFAULT_WORKERS_FALLBACK = 5

/** The two fields the table's placeholders read, from one shared response. */
interface HuntWorkerDefaults {
  workersPerCustomer: number
  graphqlWorkersPerCustomer: number
  schedulesWorkersPerCustomer: number
  huntFlow: string
}

export function useDefaultWorkers(enabled = true): number {
  const { data } = useQuery({
    queryKey: DEFAULT_WORKERS_KEY,
    queryFn: () => apiGet<HuntWorkerDefaults>('/api/automation/hunt-settings'),
    // Narrowed to the one field, so a live counter ticking inside the response
    // cannot notify this subscriber — the table would repaint for nothing.
    select: (s) => s.workersPerCustomer,
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
  return typeof data === 'number' && data > 0 ? data : DEFAULT_WORKERS_FALLBACK
}

/**
 * The fleet-wide `graphqlWorkersPerCustomer`, for the Shift list column.
 *
 * Same shape and same reasoning as `useDefaultWorkers` above, with its own key
 * and its own `select` so the two columns' placeholders cannot notify each
 * other's subscribers. React Query dedupes the underlying request, so two keys
 * against one endpoint is two cache entries, not two round trips.
 *
 * ── Floored at 1, like the server ───────────────────────────────────
 * This count sizes the fleet that reads a missing UUID off the job's listing,
 * so a stored 0 is raised to 1 the moment a fleet arms. The placeholder shows
 * what will actually run rather than what was typed, and the test is whether a
 * NUMBER arrived at all — a `> 0` guard would hide a real stored value behind
 * the fallback for as long as the setting stayed at 0.
 */
const DEFAULT_GQL_WORKERS_FALLBACK = 1

export function useDefaultGqlWorkers(enabled = true): number {
  const { data } = useQuery({
    /*
     * The SAME key as `useDefaultWorkers`, not one of its own.
     *
     * React Query dedupes by key rather than by URL, so a second key against
     * this endpoint is a second fetch on every mount and every refocus — two
     * requests to read two fields of one response. Sharing the key makes the
     * table's two placeholders one cache entry and one request; `select` still
     * narrows each hook to its own field, so neither notifies on the other's
     * change and no extra repaint follows.
     */
    queryKey: DEFAULT_WORKERS_KEY,
    queryFn: () => apiGet<HuntWorkerDefaults>('/api/automation/hunt-settings'),
    select: (s) => s.graphqlWorkersPerCustomer,
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
  // `>= 0`, where the watch's hook uses `> 0`: a stored 0 is a real value here
  // and is what the server floors, so it must not be mistaken for "not landed".
  return typeof data === 'number' && data >= 0 ? Math.max(1, data) : DEFAULT_GQL_WORKERS_FALLBACK
}

/**
 * What the GA column shows for a row with no override.
 *
 * One rather than zero, like the shift list's and unlike the watch's. A zero
 * placeholder in
 * that column would read as "this fleet is off for the whole fleet", and it
 * cannot be: without it no customer without an application ever creates one.
 */
const DEFAULT_SCHEDULE_WORKERS_FALLBACK = 1

/**
 * What the Flow column shows for a row with no override.
 *
 * `'Current'` rather than an empty placeholder, because blank in that column
 * would be indistinguishable from a value that failed to load — and this is the
 * one per-customer field where the inherited value changes what Start does, so
 * it has to be legible without opening Settings.
 */
const DEFAULT_FLOW_FALLBACK = 'current'

/**
 * The fleet-wide claim flow, for the Flow column's placeholder.
 *
 * Shares the query and the key with the worker defaults above — one request
 * answers all of them — and narrows the subscription the same way, so a live
 * counter ticking inside that response cannot repaint the table.
 */
/**
 * The fleet-wide `schedulesWorkersPerCustomer`, for the GA column's placeholder.
 *
 * Shares the query and the key with the other defaults — one request answers all
 * of them — and narrows the subscription the same way, so a live counter ticking
 * inside that response cannot repaint the table.
 */
export function useDefaultScheduleWorkers(enabled = true): number {
  const { data } = useQuery({
    queryKey: DEFAULT_WORKERS_KEY,
    queryFn: () => apiGet<HuntWorkerDefaults>('/api/automation/hunt-settings'),
    select: (s) => s.schedulesWorkersPerCustomer,
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
  return typeof data === 'number' && data >= 1 ? data : DEFAULT_SCHEDULE_WORKERS_FALLBACK
}

export function useDefaultFlow(enabled = true): string {
  const { data } = useQuery({
    queryKey: DEFAULT_WORKERS_KEY,
    queryFn: () => apiGet<HuntWorkerDefaults>('/api/automation/hunt-settings'),
    select: (s) => s.huntFlow,
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  })
  return typeof data === 'string' && data.length > 0 ? data : DEFAULT_FLOW_FALLBACK
}

/**
 * One customer's live state, or null.
 *
 * Selected rather than indexed out of `useRunStates()`, and the difference is a
 * real one at scale. Every `run.state` frame — for ANY customer — rewrites the
 * whole map, so a component that subscribes to the map re-renders on all of
 * them. With six hundred customers streaming, the open drawer was repainting
 * hundreds of times a second to display one customer whose state had not
 * changed.
 *
 * `select` narrows the subscription: react-query compares the SELECTED value
 * and only notifies when it actually differs. Unrelated customers' frames
 * produce the same object reference here and are dropped without a render.
 * Both hooks share one query key, so this costs no extra fetch.
 */
export function useRunState(customerId: string | null): RunStateDto | null {
  const qc = useQueryClient()
  const { data } = useQuery({
    ...runStatesOptions(qc),
    select: (map: RunStateMap) => (customerId === null ? null : (map[customerId] ?? null)),
  })
  return data ?? null
}

/*
 * There is deliberately no `ACTIVE_PHASES` set or `useRunningSet()` here.
 *
 * The table needs each row's FULL state — phase, detail, counters — not just
 * membership of a set, and it reads that straight off the map. A set would have
 * been a second, lossier view of the same data, recomputed on every frame, for
 * no reader. The one place that classifies a phase as "running" is the table's
 * own `RUN_ACTIVE`, next to the button logic it drives.
 */

/* ------------------------------------------------------------------ *
 * The run log
 * ------------------------------------------------------------------ */

/**
 * Ceiling on the merged view, matching the cap `use-realtime` applies to
 * streamed lines. Without it a drawer left open across many delta fetches would
 * accumulate more history than the server would ever return in one response.
 *
 * Twice the server's ring (`HIRING_RUN_LOG_LIMIT`, now 600), which is the
 * relationship this has always had — the headroom is what lets a console open
 * across several runs keep lines the ring has since evicted. Below the ring it
 * would be the binding cap and the depth on the server would never reach the
 * screen, so the two must be raised together.
 */
const MAX_SEEDED_LINES = 1_200

/**
 * One customer's run log, seeded once and then streamed.
 *
 * The response bundles the log, the counters and the heartbeat strip, so opening
 * a console is ONE request rather than three. Everything after that arrives over
 * the bus.
 */
export function useRunLog(customerId: string | null) {
  const qc = useQueryClient()

  return useQuery({
    queryKey: runLogKey(customerId ?? ''),
    queryFn: async (): Promise<RunLogResponse> => {
      const key = customerId ?? ''
      const live = qc.getQueryData<RunLogResponse>(runLogKey(key))

      /*
       * Ask for a DELTA when we already hold part of this log.
       *
       * Every refetch is a reconnect or a manual invalidate, and re-reading the
       * whole ring to discover that nothing changed is the shape this drawer
       * used to have against Mongo. The cursor is a stream id, so the server
       * ranges forward from it exclusively.
       *
       * Only when we hold lines as well as a cursor: a cursor with an empty
       * cache would fetch a delta onto nothing and lose the history behind it.
       */
      const cursor = live?.cursor
      const delta = typeof cursor === 'string' && (live?.lines.length ?? 0) > 0
      const url = `/api/customers/${encodeURIComponent(key)}/run-log${
        delta ? `?after=${encodeURIComponent(cursor)}` : ''
      }`
      const res = await apiGet<RunLogResponse>(url)

      /*
       * Merge, whichever kind of response this is.
       *
       * For a delta the cached lines ARE the history and the response is the
       * tail. For a full seed the two overlap and the dedupe below is what
       * stops a line rendering twice — lines pushed over the bus while this
       * request was in flight are already in the cache and the response can
       * carry them too.
       *
       * Keyed on `(at, message)` rather than an id because these have none:
       * they are ring entries, not documents.
       */
      const held = live?.lines ?? []
      if (held.length === 0) return res

      const seen = new Set(res.lines.map((l) => `${l.at}|${l.message}`))
      const missing = held.filter((l) => !seen.has(`${l.at}|${l.message}`))
      if (missing.length === 0) return res

      const lines = [...res.lines, ...missing].sort((a, b) => a.at - b.at)
      // Never let the merged view exceed what the server would hand back whole.
      const capped = lines.length > MAX_SEEDED_LINES ? lines.slice(lines.length - MAX_SEEDED_LINES) : lines
      return { ...res, lines: capped }
    },
    enabled: customerId !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    /*
     * Still no polling here, and `hunt` on this response is only a SEED.
     *
     * It briefly had `refetchInterval: (q) => (q.state.data?.hunt ? 1_000 : ...)`
     * to drive the worker table, which could never work: this query is fetched
     * once, when the drawer opens, and the hunt does not begin until the
     * application is created a minute or so later. The first response therefore
     * always had `hunt: null`, the interval evaluated to false, and nothing ever
     * fetched again to discover otherwise — a gate that could only ever open if
     * it were already open. `useHuntLive` below is the live one.
     */
    /*
     * Deliberately NO `placeholderData: (prev) => prev`.
     *
     * In react-query v5 that is the `keepPreviousData` replacement, and it keeps
     * the last defined data ACROSS KEY CHANGES. The drawer is permanently
     * mounted inside the table, so its observer survives every open and close —
     * which meant opening customer B's console rendered customer A's log lines,
     * under B's name, with the header and the phase pill (a different query) all
     * correctly showing B. Worse, the placeholder makes `status: 'success'`, so
     * `isLoading` is false and not even a "Loading…" hint appears; auto-scroll
     * then makes the wrong log look authoritative.
     *
     * A blank frame for one round trip is a much smaller cost than a log that
     * confidently attributes one customer's run to another.
     */
  })
}

/* ------------------------------------------------------------------ *
 * The live hunt
 * ------------------------------------------------------------------ */

/**
 * The one thing on this screen that genuinely has to be polled.
 *
 * Everything else here is pushed, and the comment at the top of this file is
 * about how hard that was won. A hunt is the exception, and deliberately so: it
 * completes tens of checks a second, and pushing each one would put more frames
 * on the bus in a minute than the entire sign-in story produces in a run — for
 * a table that can only be read at human speed anyway. Sampling the current
 * state a couple of times a second shows the operator exactly the same thing
 * for a thousandth of the traffic.
 *
 * The server side is a Map lookup, so the cost of this interval is a round trip
 * and nothing else. It runs only while the drawer is open on a live run.
 */
/**
 * Keyed by CUSTOMER only — the selected worker is deliberately not in here.
 *
 * Putting it in the key looks right and is worse: the key changing means a cache
 * miss, so every click on a worker would blank the entire panel — stages,
 * counters, worker table and all — for one round trip, to change one list.
 *
 * The response carries `logWorker`, so it says which worker's records it holds.
 * Reading that is a stricter check than a cache key anyway: it cannot be fooled
 * by a request that is still in flight, and it lets the rest of the panel keep
 * updating throughout.
 */
export function huntKey(customerId: string) {
  return ['automation', 'hunt', customerId] as const
}

/** What the drawer's live poll holds: the hunt, and the heartbeat beside it. */
export interface HuntLiveResponse {
  hunt: HuntLiveDto | null
  at: number
  /**
   * This customer's heartbeat, as of this poll. Null when none is registered;
   * absent from an API that predates the field, which is how a drawer
   * mid-deploy knows to fall back to the run-log seed.
   */
  keepAlive?: KeepAliveEntry | null
  /**
   * This browser's clock when the answer landed — the moment `nextInMs` counts
   * from. Stamped here rather than taken from `at`, which is the SERVER's
   * clock and would put any skew between the two straight into the countdown.
   */
  receivedAt: number
}

/* ------------------------------------------------------------------ *
 * The full trace
 * ------------------------------------------------------------------ */

/**
 * Every check the server recorded, fetched WHOLE and only when asked for.
 *
 * Deliberately not part of `useHuntLive`. That query runs every 700ms for as
 * long as a drawer is open; a trace is up to two thousand records and is read
 * once, by one person, while diagnosing one customer. Riding it on the poll
 * would mean paying for it continuously to use it occasionally — so it is its
 * own query with no interval, refetched by an explicit button.
 */
export function huntTraceKey(customerId: string) {
  return ['automation', 'hunt-trace', customerId] as const
}

export function useHuntTrace(customerId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: huntTraceKey(customerId ?? ''),
    queryFn: () =>
      apiGet<{ customerId: string; on: boolean; records: HuntPoll[] }>(
        `/api/customers/${encodeURIComponent(customerId ?? '')}/hunt/trace`
      ),
    enabled: customerId !== null && enabled,
    // No interval at all: the operator decides when to take a fresh snapshot.
    refetchInterval: false,
    staleTime: 0,
    gcTime: 60_000,
  })
}

/** Start or stop recording every check for one customer. */
export function useToggleHuntTrace(customerId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (on: boolean) =>
      apiPost<{ customerId: string; on: boolean }>(
        `/api/customers/${encodeURIComponent(customerId ?? '')}/hunt/trace`,
        { on }
      ),
    onSuccess: () => {
      // The hunt view carries `trace.on`, so the switch reflects the SERVER's
      // answer rather than what was clicked — a toggle that lies about state it
      // does not own is worse than one that takes a moment to settle.
      void qc.invalidateQueries({ queryKey: huntKey(customerId ?? '') })
      void qc.invalidateQueries({ queryKey: huntTraceKey(customerId ?? '') })
    },
  })
}

const HUNT_POLL_MS = 700

/**
 * @param running whether the RUN is active — not whether a hunt exists yet.
 *
 * The distinction is load-bearing at both ends. A hunt does not start until
 * roughly a minute into a run, so polling gated on "a hunt is present" would
 * never discover the first one. And a hunt that has ENDED is still worth
 * fetching once: the server keeps the finished snapshot, so reopening the
 * drawer on a stopped customer shows what its workers were doing when it
 * stopped instead of a blank panel.
 *
 * @param worker the worker whose call log and kept failures to fetch WITH the
 * view, or null for neither. Those two are the only expensive things in this
 * response — a couple of hundred records and forty response bodies — so they
 * are asked for by the person reading one worker and nobody else. Passing null
 * makes this exactly the request it has always been.
 */
export function useHuntLive(
  customerId: string | null,
  running: boolean,
  worker: number | null = null
) {
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: huntKey(customerId ?? ''),
    queryFn: async (): Promise<HuntLiveResponse> => {
      const key = customerId ?? ''
      const prev = qc.getQueryData<HuntLiveResponse>(huntKey(key))?.hunt

      /*
       * Ask for a DELTA of the two accumulating lists when we already hold some.
       *
       * The server keeps every claim attempt and every sighting of a run — there
       * is no window on either any more — so a poll that re-fetched them whole
       * would grow with the run, twice a second, forever. Handing back the newest
       * id we hold turns each into "what happened in the last 700ms", which is
       * almost always nothing at all.
       *
       * Both conditions matter for each. A cursor with no rows behind it would
       * fetch a delta onto an empty list and silently drop the history it is a
       * cursor into — the same trap the run log documents.
       */
      const strikeCursor = prev?.strikeCursor
      const wantStrikeDelta =
        typeof strikeCursor === 'string' && (prev?.strikes.length ?? 0) > 0
      const activeCursor = prev?.activeCursor
      const wantActiveDelta =
        typeof activeCursor === 'string' && (prev?.activeHits?.length ?? 0) > 0

      const params = new URLSearchParams()
      if (worker !== null) params.set('worker', String(worker))
      if (wantStrikeDelta) params.set('strikesAfter', strikeCursor)
      if (wantActiveDelta) params.set('activeAfter', activeCursor)
      const qs = params.toString()

      const res: HuntLiveResponse = {
        ...(await apiGet<Omit<HuntLiveResponse, 'receivedAt'>>(
          `/api/customers/${encodeURIComponent(key)}/hunt${qs ? `?${qs}` : ''}`
        )),
        receivedAt: Date.now(),
      }

      if (res.hunt === null) return res

      /*
       * Only merge onto the SAME run.
       *
       * `startedAt` is the hunt's identity here: pressing Stop and Start again
       * clears the server's streams and starts new ones, and appending the new
       * run's rows to the old run's list would produce a panel that blames one
       * run for another's refusals. A customer change cannot reach this — the
       * query is keyed by customer — but a restart very much can.
       */
      if (prev?.customerId !== res.hunt.customerId || prev.startedAt !== res.hunt.startedAt) {
        return res
      }

      /*
       * Append, deduped, and keep the cursor when nothing new arrived.
       *
       * A seed overlaps whatever we already had, and a degraded response falls
       * back to the server's in-memory tail — by definition rows we have seen.
       * The key is the same tuple the rows are keyed by in the tab, so a list and
       * its count cannot disagree about what "the same row" means.
       *
       * A null cursor on the response means "nothing since", not "start again":
       * a quiet poll produces one constantly, and taking it literally would
       * re-seed the whole run on the very next request.
       */
      function merge<T>(
        held: T[],
        incoming: T[],
        idOf: (row: T) => string,
        atOf: (row: T) => number
      ): T[] {
        if (held.length === 0) return incoming
        const seen = new Set(held.map(idOf))
        const fresh = incoming.filter((row) => !seen.has(idOf(row)))
        if (fresh.length === 0) return held
        return [...held, ...fresh].sort((a, b) => atOf(a) - atOf(b))
      }

      const heldStrikes = prev.strikes ?? []
      const heldHits = prev.activeHits ?? []

      return {
        ...res,
        hunt: {
          ...res.hunt,
          strikes: merge(
            heldStrikes,
            res.hunt.strikes,
            (s) => `${s.at}|${s.attempt}|${s.worker ?? ''}`,
            (s) => s.at
          ),
          strikeCursor: res.hunt.strikeCursor ?? strikeCursor ?? null,
          activeHits: merge(
            heldHits,
            res.hunt.activeHits ?? [],
            (h) => `${h.at}|${h.worker}|${h.seq}`,
            (h) => h.at
          ),
          activeCursor: res.hunt.activeCursor ?? activeCursor ?? null,
        },
      }
    },
    // Enabled whenever the drawer is open, so a finished hunt is fetched once.
    enabled: customerId !== null,
    /*
     * Poll while there is something that can still change, and not otherwise.
     *
     * Either condition alone is wrong: `running` alone keeps polling a hunt
     * that already won while its run holds the shift, and `hunt.live` alone
     * never starts, because the first response predates the hunt. Together they
     * cover the whole life — and both go false at the end, so a finished hunt
     * sits on screen costing exactly nothing.
     */
    refetchInterval: (q) => (running || q.state.data?.hunt?.live === true ? HUNT_POLL_MS : false),
    /*
     * Keep polling with the tab in the background.
     *
     * The whole point of a hunt is that it runs unattended for hours; an
     * operator who tabs away and comes back to a table frozen at the moment
     * they left would reasonably conclude the workers had stopped. It is one
     * small request.
     */
    refetchIntervalInBackground: true,
    staleTime: 0,
    /*
     * A blank frame between customers, rather than the previous one's workers.
     *
     * Same reasoning as the run log's note below: the drawer is permanently
     * mounted, so without this, opening customer B shows A's workers under B's
     * name — and worker tables are exactly the kind of thing an operator reads
     * without re-checking whose they are.
     */
    placeholderData: undefined,
    retry: false,
  })

  /*
   * Fetch NOW when the selected worker changes.
   *
   * The worker is in the URL but not in the query key — deliberately, because a
   * key change is a cache miss and would blank the whole panel to change one
   * list. The cost of that choice is that React Query has no idea the request
   * has changed: `queryFn` closing over a new value is not a reason for it to
   * refetch, so a click would sit there for up to a poll interval before the
   * log appeared. One refetch on the transition closes that.
   *
   * `previous` is what keeps it to the transition. Without it this fires on
   * every render — including the ones the poll itself causes — and the interval
   * becomes "as fast as responses arrive".
   */
  const previous = React.useRef(worker)
  const refetch = query.refetch
  React.useEffect(() => {
    if (previous.current === worker) return
    previous.current = worker
    if (customerId !== null) void refetch()
  }, [worker, customerId, refetch])

  return query
}

/* ------------------------------------------------------------------ *
 * Controls
 * ------------------------------------------------------------------ */

/**
 * Begin a run.
 *
 * Fire-and-forget: the server claims the per-customer lock before responding
 * (409 if it is already running) and the lines arrive over the stream as the run
 * progresses. That is what lets the operator close the console, or reload the
 * page, without the run being tied to their connection.
 */
/**
 * What one Start carries.
 *
 * `gender` is the answer to the one question `/start` refuses without — the
 * audio assessment has no default voice. Sent only when the operator has just
 * been asked, so the ordinary Start is exactly the request it always was; the
 * server persists it to the customer before the run begins, which is why a
 * Start and a "set the gender" are one request rather than two.
 */
export interface StartRequest {
  customerId: string
  gender?: Gender
}

export function useStartAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, gender }: StartRequest) =>
      apiPost<{ ok: boolean; customerId: string; accepted: boolean }>(
        `/api/customers/${encodeURIComponent(customerId)}/start`,
        gender === undefined ? {} : { gender }
      ),
    /*
     * Mark the run started ON CLICK, not when the server answers.
     *
     * `onMutate` fires synchronously as `mutate()` is called; `onSuccess` fires
     * a round trip later. That difference is the whole bug it replaces: for the
     * ~200ms of the POST — plus however long until the first `run.state` frame —
     * the row still rendered a live, enabled **Start** button. An operator who
     * clicks it twice, or clicks and closes the console and clicks again, sends
     * that customer a SECOND verification code and burns a real OTP attempt.
     *
     * So the button flips within one frame of the click, and the server's own
     * frame replaces this guess wholesale a moment later.
     */
    onMutate: ({ customerId }): { previous: RunStateDto | null } => {
      const previous = qc.getQueryData<RunStateMap>(RUNS_KEY)?.[customerId] ?? null

      /*
       * Drop the previous run's console BEFORE this one starts.
       *
       * The server clears its copy on Start, but that alone does not clean the
       * screen: both queries are keyed by CUSTOMER and hold `staleTime:
       * Infinity`, so the browser keeps serving the last run's log lines and its
       * final worker table from cache — and a re-run of the same customer is the
       * single most common way this drawer is opened. The operator would read an
       * hour-old "OTP timed out" as though it had just happened.
       *
       * `removeQueries`, not `invalidateQueries`: invalidation refetches but
       * keeps showing the stale data until the response lands, which is exactly
       * the frame that misleads. Removing empties them now and the seed arrives
       * on its own.
       */
      qc.removeQueries({ queryKey: runLogKey(customerId) })
      qc.removeQueries({ queryKey: huntKey(customerId) })

      qc.setQueryData<RunStateMap>(RUNS_KEY, (prev) => {
        // Never CREATE the query here — see the note in `applyRunStates`. If the
        // seed has not landed yet, the row keeps its previous button for one more
        // beat, which is strictly better than suppressing the seed forever.
        if (!prev) return prev
        const now = Date.now()
        return {
          ...prev,
          [customerId]: {
            customerId,
            runId: previous?.runId ?? null,
            phase: 'starting',
            /*
             * The optimistic frame is a guess about the SERVER, and the server
             * is about to have machinery for this customer. `starting` is in
             * `RUN_ACTIVE` so the button already flips either way — this keeps
             * the two fields consistent so nothing downstream reads a run that
             * is starting and simultaneously claims nothing is running.
             */
            live: true,
            stepKey: null,
            detail: null,
            startedAt: now,
            updatedAt: now,
            applicationId: null,
            applicationState: null,
            // Carried so the console does not blank the endpoint mid-restart.
            egress: previous?.egress ?? null,
            // Same for the identity pair: a re-run reuses the reservation, so
            // clearing these would flicker the header empty for the couple of
            // seconds until sign-in re-states them.
            email: previous?.email ?? null,
            // Carried across, like the email and the endpoint: a customer's name
            // cannot change between two runs, and blanking it would leave the
            // header unlabelled for the ten seconds before sign-in re-states it.
            name: previous?.name ?? null,
            agent: previous?.agent ?? null,
            metrics: {
              calls: 0,
              ok2xx: 0,
              s403: 0,
              s405: 0,
              s429: 0,
              otherErrors: 0,
              lastRttMs: null,
              lastOkRttMs: null,
              meanRttMs: null,
              waf405ToOkMs: null,
              activeToOkMs: null,
              // Carried across, not blanked: the token's age is a fact about the
              // CREDENTIAL, and a run restarted on a reused session must not
              // report a ninety-minute-old token as brand new.
              signedInAtMs: previous?.metrics.signedInAtMs ?? null,
              beats: 0,
              retries: 0,
              warns: 0,
              errors: 0,
              elapsedMs: 0,
            },
          },
        }
      })

      return { previous }
    },

    /*
     * Put the row back exactly as it was.
     *
     * A rejected start (404 no credentials on file, 409 already running) must
     * not leave the row showing a run that never began — that row would offer
     * Stop for nothing and never offer Start again, since no `run.state` frame
     * is coming to correct it.
     */
    onError: (_err, { customerId }, context) => {
      qc.setQueryData<RunStateMap>(RUNS_KEY, (prev) => {
        if (!prev) return prev
        const next = { ...prev }
        if (context?.previous) next[customerId] = context.previous
        else delete next[customerId]
        return next
      })
    },
    // Success needs no reconciliation: the `run.state` frame is already on its
    // way, and refetching for it would undo the point of the push.
  })
}

export function useStopAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (customerId: string) =>
      apiPost<{ ok: boolean; customerId: string; wasRunning: boolean }>(
        `/api/customers/${encodeURIComponent(customerId)}/stop`,
        {}
      ),
    // The server pushes `stopped` from its own stop path, so the only case worth
    // refetching is the one where the request never landed.
    onError: () => {
      void qc.invalidateQueries({ queryKey: RUNS_KEY })
    },
  })
}
